import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Facebook, Instagram, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import analytics from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState([]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/categories`);
        // Filter out bundles for shop section (show only product categories)
        const productCategories = response.data.filter(cat => cat.slug !== 'bundles-starter-kits');
        setCategories(productCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email');
      return;
    }

    setStatus('loading');
    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      setStatus('success');
      setMessage(response.data.message);
      setEmail('');
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to subscribe');
    }
  };

  return (
    <footer className="bg-secondary border-t border-border mt-0" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-1">
            <Link to="/" className="inline-flex items-center mb-4" data-testid="footer-logo" aria-label="GoodHabits Family — Home">
              <img
                src="/images/logos/logo-horizontal.png"
                alt="GoodHabits Family"
                className="h-12 w-auto"
                width="220"
                height="75"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
              Empowering families with digital resources for healthier, happier children.
            </p>
            <div className="flex items-center space-x-4" data-testid="footer-social-links">
              <a
                href="https://www.facebook.com/profile.php?id=61587164416490"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GoodHabits Family on Facebook"
                onClick={() => analytics.trackEvent('social_link_click', { platform: 'facebook', placement: 'footer' })}
                className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm inline-flex items-center justify-center h-8 w-8"
                data-testid="footer-social-facebook"
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/goodhabits_family/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GoodHabits Family on Instagram"
                onClick={() => analytics.trackEvent('social_link_click', { platform: 'instagram', placement: 'footer' })}
                className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm inline-flex items-center justify-center h-8 w-8"
                data-testid="footer-social-instagram"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href="https://pin.it/5BBHa5OEQ"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GoodHabits Family on Pinterest"
                onClick={() => analytics.trackEvent('social_link_click', { platform: 'pinterest', placement: 'footer' })}
                className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm inline-flex items-center justify-center h-8 w-8"
                data-testid="footer-social-pinterest"
              >
                {/* Inline Pinterest logomark — lucide-react has no Pinterest icon */}
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-5 w-5"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.098.119.112.224.083.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.696-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379 0 0-.599 2.282-.744 2.84-.282 1.084-1.064 2.456-1.549 3.235C9.584 23.815 10.77 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641 0 12.017 0z"/>
                </svg>
              </a>
              <a
                href="https://goodhabitsfamily.etsy.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Shop GoodHabits Family on Etsy"
                onClick={() => analytics.trackEvent('social_link_click', { platform: 'etsy', placement: 'footer' })}
                className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm inline-flex items-center justify-center h-8 min-w-8 px-1 text-sm font-semibold leading-none tracking-tight"
                data-testid="footer-social-etsy"
              >
                Etsy
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Shop</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/shop" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  All Toolkits
                </Link>
              </li>
              <li>
                <Link to="/challenges" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Browse by Challenge
                </Link>
              </li>
              <li>
                <Link to="/free-resources" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Free Resources
                </Link>
              </li>
              {categories.map((category) => (
                <li key={category.id}>
                  <Link 
                    to={`/category/${category.slug}`} 
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/help" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/track-order" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Track an Order
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/page/terms" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/page/privacy" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/page/payment-methods" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                  Payment Methods
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Stay Connected</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Get tips, resources, and exclusive offers delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit}>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'loading' || status === 'success'}
                  className="flex-1 px-4 py-2 border border-border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-sm disabled:bg-muted"
                  data-testid="newsletter-email-input"
                />
                <button 
                  type="submit"
                  disabled={status === 'loading' || status === 'success'}
                  className="bg-primary text-primary-foreground px-4 py-2 rounded-r-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="newsletter-submit-btn"
                >
                  {status === 'loading' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : status === 'success' ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Mail className="h-5 w-5" />
                  )}
                </button>
              </div>
              {message && (
                <p className={`text-xs mt-2 ${status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="text-center space-y-2">
            {/* Mission reminder */}
            <p className="text-sm italic" style={{ color: 'var(--muted)' }}>
              Supporting families with simple tools for healthier habits, calmer routines, and stronger connections.
            </p>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              © 2026 GoodHabits Family. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;